import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PERMISSIONS } from '@lms/shared-types';
import { Alert, Loader, Modal } from '@lms/ui';
import { AssessmentAttemptReview } from '@/components/assessments/AssessmentAttemptReview';
import { AttemptStaffFlags } from '@/components/assessments/AttemptStaffFlags';
import { HubDetailPageHeader } from '@/components/layout/HubDetailPageHeader';
import { usePermissions } from '@/hooks/usePermissions';
import { api } from '@/lib/api';
import { staffAttemptActions } from '@/lib/assessment-attempt-actions';
import {
  formatScoreFraction,
  formatScorePercentage,
  showsScoreAndPercent,
} from '@/lib/assessment-score-display';

interface StaffAttemptRow {
  id: string;
  number: number;
  userId: string;
  studentName: string;
  studentEmail: string;
  date: string;
  startedAt: string;
  submittedAt: string | null;
  durationSeconds: number | null;
  answeredCount: number;
  questionCount: number;
  status: string;
  score: number | null;
  maxScore: number;
  result: 'pass' | 'fail' | 'in_progress' | null;
  late?: boolean;
  submissionSource?: string | null;
  hasPendingLateAnswers?: boolean;
  multiDevice?: boolean;
  hasConnectionGaps?: boolean;
}

interface StaffAttemptsPayload {
  assessment: {
    id?: string;
    title: string;
    code?: string;
    type?: string;
    currentVersion?: {
      settings?: { require_passing_score?: boolean; show_score_and_percent?: boolean } | null;
    };
  };
  attempts: StaffAttemptRow[];
}

const TYPE_PREFIX: Record<string, string> = {
  exam: 'Examen: ',
  quiz: 'Quiz: ',
  survey: 'Encuesta: ',
};

const STATUS_LABELS: Record<string, string> = {
  submitted: 'Finalizado',
  in_progress: 'En progreso',
  pending_review: 'En revisión',
  approved: 'Finalizado',
  rejected: 'Finalizado',
  expired_empty: 'Vencido sin respuestas',
};

const RESULT_LABELS: Record<string, string> = {
  pass: 'Aprobado',
  fail: 'Reprobado',
  in_progress: 'En progreso',
};

function formatDateTime(value: string): string {
  return new Date(value).toLocaleString('es', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatDuration(seconds: number | null | undefined, status: string): string {
  if (seconds == null || !Number.isFinite(seconds)) return status === 'in_progress' ? 'En curso' : '—';
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const rest = seconds % 60;
  if (hours > 0) return rest > 0 || minutes > 0 ? `${hours} h ${minutes} min` : `${hours} h`;
  if (minutes > 0) return rest > 0 ? `${minutes} min ${rest} s` : `${minutes} min`;
  return `${rest} s`;
}

function formatAnswered(answered: number | null | undefined, total: number | null | undefined): string {
  if (total == null || total <= 0) return '—';
  return `${answered ?? 0} / ${total}`;
}

function studentInitials(name: string, email: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0][0] ?? ''}${parts[1][0] ?? ''}`.toUpperCase();
  }
  if (parts[0] && parts[0].length >= 2) {
    return parts[0].slice(0, 2).toUpperCase();
  }
  return email.slice(0, 2).toUpperCase();
}

interface AssessmentStaffAttemptsViewProps {
  assessmentInstanceId: string;
  backTo: string;
  backLabel?: string;
  itemTitle?: string;
}

export function AssessmentStaffAttemptsView({
  assessmentInstanceId,
  backTo,
  backLabel = 'Volver al curso',
  itemTitle,
}: AssessmentStaffAttemptsViewProps) {
  const queryClient = useQueryClient();
  const { hasPermission } = usePermissions();
  const canManageAttempts = hasPermission(PERMISSIONS.EVALUACIONES_INTENTOS_GESTIONAR);
  const staffPermissions: string[] = [];
  if (hasPermission(PERMISSIONS.EVALUACIONES_VER)) staffPermissions.push(PERMISSIONS.EVALUACIONES_VER);
  if (hasPermission(PERMISSIONS.CURSOS_SUPERVISAR)) staffPermissions.push(PERMISSIONS.CURSOS_SUPERVISAR);
  if (canManageAttempts) staffPermissions.push(PERMISSIONS.EVALUACIONES_INTENTOS_GESTIONAR);
  const [reviewAttemptId, setReviewAttemptId] = useState<string | null>(null);
  const [reviewReadOnly, setReviewReadOnly] = useState(true);
  const [reviewEditMode, setReviewEditMode] = useState(false);
  const [deletingAttemptId, setDeletingAttemptId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [quickReviewAttempt, setQuickReviewAttempt] = useState<StaffAttemptRow | null>(null);
  const [quickReviewComment, setQuickReviewComment] = useState('');

  const queryKey = ['assessment-staff-attempts', assessmentInstanceId] as const;

  const { data, isLoading, error } = useQuery({
    queryKey,
    queryFn: () =>
      api<StaffAttemptsPayload>(`/assessments/instances/${assessmentInstanceId}/staff-attempts`),
    enabled: Boolean(assessmentInstanceId),
  });
  const showGrades = showsScoreAndPercent(data?.assessment.currentVersion?.settings);

  const deleteAttemptMutation = useMutation({
    mutationFn: (attemptId: string) =>
      api(`/assessments/attempts/${attemptId}`, { method: 'DELETE' }),
    onSuccess: () => {
      setDeletingAttemptId(null);
      void queryClient.invalidateQueries({ queryKey });
    },
    onError: () => {
      setDeletingAttemptId(null);
    },
  });

  const quickReviewMutation = useMutation({
    mutationFn: (decision: 'approved' | 'rejected') => {
      if (!quickReviewAttempt) throw new Error('No hay un intento seleccionado');
      return api(`/assessments/attempts/${quickReviewAttempt.id}/quick-review`, {
        method: 'POST',
        body: { decision, comment: quickReviewComment.trim() },
      });
    },
    onSuccess: () => {
      setQuickReviewAttempt(null);
      setQuickReviewComment('');
      void queryClient.invalidateQueries({ queryKey });
    },
  });

  const handleDeleteAttempt = (attemptId: string) => {
    if (!window.confirm('¿Eliminar este intento? Esta acción no se puede deshacer.')) return;
    setDeletingAttemptId(attemptId);
    deleteAttemptMutation.mutate(attemptId);
  };

  const assessmentType = data?.assessment.type ?? 'exam';
  const titlePrefix = TYPE_PREFIX[assessmentType] ?? '';
  const title = `${titlePrefix}${data?.assessment.title ?? itemTitle ?? 'Evaluación'}`;

  const filteredAttempts = useMemo(() => {
    const attempts = data?.attempts ?? [];
    const query = searchQuery.trim().toLowerCase();
    if (!query) return attempts;
    return attempts.filter(
      (attempt) =>
        attempt.studentName.toLowerCase().includes(query) ||
        attempt.studentEmail.toLowerCase().includes(query),
    );
  }, [data?.attempts, searchQuery]);

  const totalAttempts = data?.attempts.length ?? 0;
  const visibleCount = filteredAttempts.length;

  if (reviewAttemptId) {
    const reviewAttempt = data?.attempts.find((attempt) => attempt.id === reviewAttemptId);

    return (
      <AssessmentAttemptReview
        attemptId={reviewAttemptId}
        backTo={backTo}
        backLabel={backLabel}
        attemptNumber={reviewAttempt?.number}
        titlePrefix={titlePrefix}
        canManageAttempts={canManageAttempts && !reviewReadOnly}
        initialEditMode={reviewEditMode}
        onBack={() => {
          setReviewAttemptId(null);
          setReviewReadOnly(true);
          setReviewEditMode(false);
          void queryClient.invalidateQueries({ queryKey });
        }}
      />
    );
  }

  return (
    <div className="lms-student-attempts font-sans">
      <div className="lms-site-admin-hub-container py-8 md:py-12">
        <HubDetailPageHeader
          backTo={backTo}
          backLabel={backLabel}
          title={title}
          description={data?.assessment.code ?? undefined}
        />

        {deleteAttemptMutation.error && (
          <Alert variant="danger" className="mb-4">
            {(deleteAttemptMutation.error as Error).message}
          </Alert>
        )}

        {isLoading ? (
          <div className="flex justify-center py-12">
            <Loader label="Cargando intentos…" />
          </div>
        ) : error ? (
          <Alert variant="danger">{(error as Error).message}</Alert>
        ) : (
          <>
            <div className="lms-student-attempts__section-header">
              <h2 className="lms-student-attempts__section-title">Intentos de estudiantes</h2>
              <div className="lms-student-attempts__search">
                <span
                  className="material-symbols-outlined lms-student-attempts__search-icon"
                  aria-hidden="true"
                >
                  search
                </span>
                <input
                  type="search"
                  className="lms-student-attempts__search-input"
                  placeholder="Buscar estudiante..."
                  value={searchQuery}
                  onChange={(event) => setSearchQuery(event.target.value)}
                  aria-label="Buscar estudiante"
                />
              </div>
            </div>

            <div className="lms-student-attempts__card">
              <div className="lms-student-attempts__table-scroll">
                {totalAttempts === 0 ? (
                  <p className="lms-student-attempts__empty">
                    No hay intentos registrados por estudiantes.
                  </p>
                ) : filteredAttempts.length === 0 ? (
                  <p className="lms-student-attempts__empty">
                    No se encontraron estudiantes con ese criterio.
                  </p>
                ) : (
                  <table className="lms-student-attempts__table">
                    <thead>
                      <tr>
                        <th>Estudiante</th>
                        <th className="col-center">Intento</th>
                        <th>Inicio</th>
                        <th>Final</th>
                        <th>Duración</th>
                        <th className="col-center">Preguntas</th>
                        <th>Estado</th>
                        {showGrades && <th className="col-right">Calificación</th>}
                        {showGrades && <th className="col-center">%</th>}
                        <th>Resultado</th>
                        <th>Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredAttempts.map((attempt) => {
                        const actions = staffAttemptActions(attempt.status, staffPermissions);
                        return (
                        <tr key={attempt.id}>
                          <td>
                            <div className="lms-student-attempts__student">
                              <span className="lms-student-attempts__avatar" aria-hidden="true">
                                {studentInitials(attempt.studentName, attempt.studentEmail)}
                              </span>
                              <div>
                                <div className="lms-student-attempts__student-name">
                                  {attempt.studentName}
                                </div>
                                <div className="lms-student-attempts__student-email">
                                  {attempt.studentEmail}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="lms-student-attempts__cell-center">
                            <span className="lms-student-attempts__attempt-num">
                              {attempt.number}
                            </span>
                          </td>
                          <td className="lms-student-attempts__cell-muted">
                            {formatDateTime(attempt.startedAt)}
                          </td>
                          <td className="lms-student-attempts__cell-muted">
                            {attempt.submittedAt ? formatDateTime(attempt.submittedAt) : '—'}
                          </td>
                          <td className="lms-student-attempts__cell-muted col-nowrap">
                            {formatDuration(attempt.durationSeconds, attempt.status)}
                          </td>
                          <td
                            className="lms-student-attempts__cell-center col-nowrap"
                            title={`${attempt.answeredCount} de ${attempt.questionCount} preguntas respondidas`}
                          >
                            {formatAnswered(attempt.answeredCount, attempt.questionCount)}
                          </td>
                          <td>
                            <span
                              className={`lms-student-attempts__status-badge${
                                attempt.status === 'in_progress'
                                  ? ' lms-student-attempts__status-badge--progress'
                                  : ''
                              }`}
                            >
                              {STATUS_LABELS[attempt.status] ?? attempt.status}
                            </span>
                            <AttemptStaffFlags
                              late={attempt.late}
                              submissionSource={attempt.submissionSource}
                              hasPendingLateAnswers={attempt.hasPendingLateAnswers}
                              multiDevice={attempt.multiDevice}
                              hasConnectionGaps={attempt.hasConnectionGaps}
                            />
                          </td>
                          {showGrades && (
                            <td className="lms-student-attempts__cell-right">
                              {formatScoreFraction(attempt.score, attempt.maxScore)}
                            </td>
                          )}
                          {showGrades && (
                            <td className="lms-student-attempts__cell-center lms-student-attempts__cell-muted">
                              {formatScorePercentage(attempt.score, attempt.maxScore)}
                            </td>
                          )}
                          <td>
                            {attempt.status === 'pending_review' && (
                              <div className="lms-student-attempts__result">
                                <span>En revisión</span>
                              </div>
                            )}
                            {attempt.result === 'pass' && (
                              <div className="lms-student-attempts__result lms-student-attempts__result--pass">
                                <span className="material-symbols-outlined" aria-hidden="true">
                                  check
                                </span>
                                <span>{RESULT_LABELS.pass}</span>
                              </div>
                            )}
                            {attempt.result === 'fail' && (
                              <div className="lms-student-attempts__result lms-student-attempts__result--fail">
                                <span className="material-symbols-outlined" aria-hidden="true">
                                  close
                                </span>
                                <span>{RESULT_LABELS.fail}</span>
                              </div>
                            )}
                            {attempt.result === 'in_progress' && (
                              <div className="lms-student-attempts__result">
                                <span>{RESULT_LABELS.in_progress}</span>
                              </div>
                            )}
                            {attempt.result === null && attempt.status !== 'pending_review' && (
                              <span className="lms-student-attempts__cell-muted">—</span>
                            )}
                          </td>
                          <td>
                            <div className="lms-student-attempts__actions">
                                    {actions.viewDetail && (
                                      <button
                                        type="button"
                                        className="lms-student-attempts__action-btn lms-student-attempts__action-btn--view"
                                        onClick={() => {
                                          setReviewReadOnly(true);
                                          setReviewEditMode(false);
                                          setReviewAttemptId(attempt.id);
                                        }}
                                        title="Ver detalle"
                                        aria-label="Ver detalle"
                                      >
                                        <span className="material-symbols-outlined" aria-hidden="true">
                                          visibility
                                        </span>
                                      </button>
                                    )}
                                    {actions.quickReview && (
                                      <button
                                        type="button"
                                        className="lms-student-attempts__action-btn lms-student-attempts__action-btn--view"
                                        onClick={() => {
                                          setQuickReviewComment('');
                                          quickReviewMutation.reset();
                                          setQuickReviewAttempt(attempt);
                                        }}
                                        title="Revisión rápida"
                                        aria-label="Revisión rápida"
                                      >
                                        <span className="material-symbols-outlined" aria-hidden="true">
                                          bolt
                                        </span>
                                      </button>
                                    )}
                                    {actions.review && (
                                      <button
                                        type="button"
                                        className="lms-student-attempts__action-btn lms-student-attempts__action-btn--view"
                                        onClick={() => {
                                          setReviewReadOnly(false);
                                          setReviewEditMode(false);
                                          setReviewAttemptId(attempt.id);
                                        }}
                                        title="Revisar"
                                        aria-label="Revisar"
                                      >
                                        <span className="material-symbols-outlined" aria-hidden="true">
                                          fact_check
                                        </span>
                                      </button>
                                    )}
                                    {actions.edit && (
                                      <button
                                        type="button"
                                        className="lms-student-attempts__action-btn lms-student-attempts__action-btn--edit"
                                        onClick={() => {
                                          setReviewReadOnly(false);
                                          setReviewEditMode(true);
                                          setReviewAttemptId(attempt.id);
                                        }}
                                        title="Editar"
                                        aria-label="Editar"
                                      >
                                        <span className="material-symbols-outlined" aria-hidden="true">
                                          edit
                                        </span>
                                      </button>
                                    )}
                                    {actions.delete && (
                                      <button
                                        type="button"
                                        className="lms-student-attempts__action-btn lms-student-attempts__action-btn--delete"
                                        onClick={() => handleDeleteAttempt(attempt.id)}
                                        disabled={deletingAttemptId === attempt.id}
                                        title="Eliminar"
                                        aria-label="Eliminar"
                                        aria-busy={deletingAttemptId === attempt.id}
                                      >
                                        <span className="material-symbols-outlined" aria-hidden="true">
                                          {deletingAttemptId === attempt.id
                                            ? 'progress_activity'
                                            : 'delete'}
                                        </span>
                                      </button>
                                    )}
                            </div>
                          </td>
                        </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>

              {totalAttempts > 0 && (
                <footer className="lms-student-attempts__footer">
                  <div className="lms-student-attempts__footer-count">
                    Mostrando 1 a {visibleCount} de {totalAttempts} intentos
                  </div>
                  <div className="lms-student-attempts__pagination">
                    <button
                      type="button"
                      className="lms-student-attempts__page-btn"
                      disabled
                      aria-label="Página anterior"
                    >
                      <span className="material-symbols-outlined" aria-hidden="true">
                        chevron_left
                      </span>
                    </button>
                    <span className="lms-student-attempts__page-btn lms-student-attempts__page-btn--active">
                      1
                    </span>
                    <button
                      type="button"
                      className="lms-student-attempts__page-btn"
                      disabled
                      aria-label="Página siguiente"
                    >
                      <span className="material-symbols-outlined" aria-hidden="true">
                        chevron_right
                      </span>
                    </button>
                  </div>
                </footer>
              )}
            </div>
          </>
        )}
      </div>
      <Modal
        open={quickReviewAttempt != null}
        onClose={() => {
          if (quickReviewMutation.isPending) return;
          setQuickReviewAttempt(null);
        }}
        title="Revisión rápida"
        description={
          quickReviewAttempt
            ? `${quickReviewAttempt.studentName} · intento ${quickReviewAttempt.number}`
            : undefined
        }
        size="sm"
        footer={
          <div className="flex flex-col-reverse gap-2 border-t border-dash-outline-variant px-6 py-4 sm:flex-row sm:justify-end">
            <button
              type="button"
              className="rounded-lg border border-dash-outline-variant px-4 py-2 text-sm font-medium text-dash-primary"
              onClick={() => setQuickReviewAttempt(null)}
              disabled={quickReviewMutation.isPending}
            >
              Cancelar
            </button>
            <button
              type="button"
              className="rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-sm font-medium text-red-700 disabled:opacity-60"
              onClick={() => quickReviewMutation.mutate('rejected')}
              disabled={quickReviewMutation.isPending || quickReviewComment.trim().length === 0}
            >
              Rechazar
            </button>
            <button
              type="button"
              className="rounded-lg bg-dash-primary px-4 py-2 text-sm font-medium text-white disabled:opacity-60"
              onClick={() => quickReviewMutation.mutate('approved')}
              disabled={quickReviewMutation.isPending}
            >
              {quickReviewMutation.isPending ? 'Guardando…' : 'Aprobar'}
            </button>
          </div>
        }
      >
        <label className="block text-sm font-medium text-dash-on-surface" htmlFor="quick-review-comment">
          Comentario
        </label>
        <textarea
          id="quick-review-comment"
          rows={4}
          value={quickReviewComment}
          onChange={(event) => setQuickReviewComment(event.target.value)}
          placeholder="Obligatorio si rechazas el examen"
          className="mt-2 w-full rounded-lg border border-dash-outline-variant px-3 py-2 text-sm outline-none focus:border-dash-secondary focus:ring-2 focus:ring-dash-secondary/20"
        />
        {quickReviewMutation.error && (
          <Alert variant="danger" className="mt-3">
            {(quickReviewMutation.error as Error).message}
          </Alert>
        )}
      </Modal>
    </div>
  );
}
